import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import { useMyUserSignedQuery } from '../../query/hooks/useMyUserSigned';
import {
  exportSignaturePngDataUrl,
  type SignaturePadExport,
} from '../../pages/SurveyPage/components/surveyBeritaAcaraSignature';

const CANVAS_HEIGHT = 180;

type SignaturePoint = { x: number; y: number; pressure?: number; time?: number };
type SignaturePointGroup = { points: SignaturePoint[] };

type PadHandle = SignaturePadExport & {
  clear: () => void;
  toData?: () => SignaturePointGroup[];
  fromData?: (groups: SignaturePointGroup[]) => void;
};

function scalePointGroups(
  groups: SignaturePointGroup[],
  from: { w: number; h: number },
  to: { w: number; h: number },
): SignaturePointGroup[] {
  if (from.w <= 0 || from.h <= 0) return groups;
  const scale = Math.min(to.w / from.w, to.h / from.h);
  const offsetX = (to.w - from.w * scale) / 2;
  const offsetY = (to.h - from.h * scale) / 2;
  return groups.map((group) => ({
    ...group,
    points: group.points.map((point) => ({
      ...point,
      x: point.x * scale + offsetX,
      y: point.y * scale + offsetY,
    })),
  }));
}

type SignaturePadProps = {
  penColor?: string;
  backgroundColor?: string;
  clearOnResize?: boolean;
  minWidth?: number;
  maxWidth?: number;
  minDistance?: number;
  onEnd?: () => void;
  canvasProps?: React.CanvasHTMLAttributes<HTMLCanvasElement>;
};

const SignaturePad = SignatureCanvas as unknown as React.ForwardRefExoticComponent<
  SignaturePadProps & React.RefAttributes<PadHandle>
>;

export type SignatureCaptureHandle = {
  exportPng: () => string | null;
  isReady: () => boolean;
  clear: () => void;
};

type Props = {
  open: boolean;
  disabled?: boolean;
  title?: string;
  helperText?: string;
  canvasTestId?: string;
  /** When false, hide "Gunakan tanda tangan default" (e.g. profile editor, customer sign). */
  allowDefault?: boolean;
  /** Stretch the drawing area to fill a flex parent, used by the fullscreen sign dialog. */
  fillHeight?: boolean;
  /** Hide the helper line so a fullscreen pad can use the height. */
  showHelperText?: boolean;
  /** Hide Hapus / default / extra actions when the parent draws its own toolbar. */
  showActions?: boolean;
  extraActions?: React.ReactNode;
  onReadyChange?: (ready: boolean) => void;
};

export const SignatureCapture = forwardRef<SignatureCaptureHandle, Props>(function SignatureCapture(
  {
    open,
    disabled = false,
    title,
    helperText = 'Gambar tanda tangan di kotak di bawah.',
    canvasTestId = 'signature-capture-canvas',
    allowDefault = true,
    fillHeight = false,
    showHelperText = true,
    showActions = true,
    extraActions,
    onReadyChange,
  },
  ref,
) {
  const { jwt } = useAuth();
  const defaultQ = useMyUserSignedQuery(allowDefault ? jwt : null);
  const defaultSign = (defaultQ.data?.sign || '').trim();
  const hasDefault = Boolean(allowDefault && defaultSign);

  const wrapRef = useRef<HTMLDivElement>(null);
  const padRef = useRef<PadHandle | null>(null);
  const padSizeRef = useRef({ w: 0, h: CANVAS_HEIGHT });
  const paintedSizeRef = useRef({ w: 0, h: CANVAS_HEIGHT });
  const pendingRestoreRef = useRef<{
    source: SignaturePointGroup[];
    from: { w: number; h: number };
    to: { w: number; h: number };
  } | null>(null);
  const [padSize, setPadSize] = useState({ w: 0, h: CANVAS_HEIGHT });
  const [hasStroke, setHasStroke] = useState(false);
  const [appliedDefault, setAppliedDefault] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      padSizeRef.current = { w: 0, h: CANVAS_HEIGHT };
      paintedSizeRef.current = { w: 0, h: CANVAS_HEIGHT };
      pendingRestoreRef.current = null;
      setPadSize({ w: 0, h: CANVAS_HEIGHT });
      setHasStroke(false);
      setAppliedDefault(null);
      onReadyChange?.(false);
      return;
    }
    const el = wrapRef.current;
    if (!el) return;

    const measure = () => {
      const w = Math.floor(el.clientWidth);
      const h = Math.floor(fillHeight ? el.clientHeight : CANVAS_HEIGHT);
      if (w <= 0 || h <= 0) return;
      const prev = padSizeRef.current;
      if (Math.abs(prev.w - w) <= 1 && Math.abs(prev.h - h) <= 1) return;
      const painted = paintedSizeRef.current;
      const pad = padRef.current;
      const canvasMatchesPaint =
        painted.w === prev.w && painted.h === prev.h && prev.w > 0 && prev.h > 0;
      if (!canvasMatchesPaint && pendingRestoreRef.current) {
        pendingRestoreRef.current.to = { w, h };
      } else if (canvasMatchesPaint && pad && typeof pad.toData === 'function') {
        try {
          const groups = pad.toData();
          pendingRestoreRef.current =
            groups.length > 0
              ? { source: groups, from: { w: prev.w, h: prev.h }, to: { w, h } }
              : null;
        } catch {
          pendingRestoreRef.current = null;
        }
      }
      padSizeRef.current = { w, h };
      setPadSize({ w, h });
    };

    measure();
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, fillHeight, onReadyChange]);

  useLayoutEffect(() => {
    const job = pendingRestoreRef.current;
    const pad = padRef.current;
    if (job && pad?.fromData && job.to.w === padSize.w && job.to.h === padSize.h) {
      pendingRestoreRef.current = null;
      pad.fromData(scalePointGroups(job.source, job.from, job.to));
    }
    if (pad && padSize.w > 0 && padSize.h > 0) {
      paintedSizeRef.current = { w: padSize.w, h: padSize.h };
    }
  }, [padSize]);

  const markStroke = () => {
    setAppliedDefault(null);
    setHasStroke(true);
    onReadyChange?.(true);
  };

  const applyDefault = () => {
    if (!defaultSign || disabled) return;
    padRef.current?.clear();
    setHasStroke(false);
    setAppliedDefault(defaultSign);
    onReadyChange?.(true);
  };

  const clearPad = () => {
    padRef.current?.clear();
    setHasStroke(false);
    setAppliedDefault(null);
    onReadyChange?.(false);
  };

  useImperativeHandle(ref, () => ({
    exportPng: () => appliedDefault || exportSignaturePngDataUrl(padRef.current),
    isReady: () => Boolean(appliedDefault) || hasStroke,
    clear: clearPad,
  }));

  const ready = Boolean(appliedDefault) || hasStroke;

  return (
    <Box
      sx={
        fillHeight
          ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }
          : undefined
      }
    >
      {title ? (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
      ) : null}
      {showHelperText ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {helperText}
        </Typography>
      ) : null}
      <Box
        ref={wrapRef}
        data-testid={appliedDefault ? `${canvasTestId}-default-preview` : canvasTestId}
        sx={{
          width: '100%',
          height: fillHeight ? 0 : CANVAS_HEIGHT,
          flex: fillHeight ? '1 1 auto' : undefined,
          minHeight: fillHeight ? 0 : undefined,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: '#fff',
          overflow: 'hidden',
          touchAction: appliedDefault ? undefined : 'none',
          display: appliedDefault ? 'flex' : 'block',
          alignItems: appliedDefault ? 'center' : undefined,
          justifyContent: appliedDefault ? 'center' : undefined,
          p: appliedDefault ? 1 : 0,
        }}
      >
        {appliedDefault ? (
          <Box
            component="img"
            src={appliedDefault}
            alt="Tanda tangan default"
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : open && padSize.w > 0 ? (
          <SignaturePad
            ref={padRef}
            penColor="#111111"
            backgroundColor="#ffffff"
            clearOnResize={false}
            minWidth={0.8}
            maxWidth={2.4}
            minDistance={1}
            onEnd={markStroke}
            canvasProps={{
              width: padSize.w,
              height: padSize.h,
              style: {
                display: 'block',
                width: '100%',
                height: fillHeight ? '100%' : padSize.h,
                touchAction: 'none',
                cursor: disabled ? 'default' : 'crosshair',
                pointerEvents: disabled ? 'none' : 'auto',
              },
            }}
          />
        ) : (
          <Box sx={{ height: fillHeight ? '100%' : CANVAS_HEIGHT }} />
        )}
      </Box>
      {showActions ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          {hasDefault ? (
            <Button
              size="small"
              onClick={applyDefault}
              disabled={disabled || Boolean(appliedDefault)}
              data-testid={`${canvasTestId}-use-default`}
            >
              Gunakan tanda tangan default
            </Button>
          ) : null}
          <Button size="small" onClick={clearPad} disabled={!ready || disabled}>
            Hapus
          </Button>
          {extraActions}
        </Stack>
      ) : null}
    </Box>
  );
});
